import { Link } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { ResearchJob } from "@carago/shared";
import { JobStatusBadge } from "../components/JobStatusBadge.js";
import { ApiClientError } from "../lib/api.js";
import {
  canCancelResearchJob,
  canResumeResearchJob,
  cancelResearchJob,
  formatCostUsd,
  formatJobDate,
  listResearchJobs,
  resumeResearchJob,
} from "../lib/researchJobs.js";

const PAGE_SIZE = 20;

export function ResearchJobsPage() {
  const queryClient = useQueryClient();
  const listQuery = useInfiniteQuery({
    queryKey: ["research-jobs"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listResearchJobs({ cursor: pageParam, limit: PAGE_SIZE }),
    getNextPageParam: (last) => last.next_cursor,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelResearchJob(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["research-jobs"] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => resumeResearchJob(id),
    onSuccess: (job) => {
      void queryClient.invalidateQueries({ queryKey: ["research-jobs"] });
      void queryClient.invalidateQueries({
        queryKey: ["research-jobs", job.id],
      });
    },
  });

  const jobs = listQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const actionError =
    cancelMutation.error instanceof ApiClientError
      ? cancelMutation.error.message
      : resumeMutation.error instanceof ApiClientError
        ? resumeMutation.error.message
        : cancelMutation.error || resumeMutation.error
          ? "Aktion fehlgeschlagen"
          : null;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Research Jobs mit Status, Fortschritt und Kostenschätzung.
        </p>
        <Link
          to="/research-jobs/new"
          className="inline-flex rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
        >
          Neuer Job
        </Link>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-gray-500">Lade Jobs…</p>
      ) : null}

      {listQuery.isError ? (
        <div className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800">
          {listQuery.error instanceof ApiClientError
            ? listQuery.error.message
            : "Jobs konnten nicht geladen werden."}
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      ) : null}

      {!listQuery.isLoading && !listQuery.isError && jobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <p className="text-sm text-gray-600">Noch keine Research Jobs.</p>
          <Link
            to="/research-jobs/new"
            className="mt-4 inline-flex rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
          >
            Ersten Job erstellen
          </Link>
        </div>
      ) : null}

      {jobs.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Query / Region</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Kosten</th>
                <th className="px-4 py-3">Erstellt</th>
                <th className="px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  busy={
                    (cancelMutation.isPending &&
                      cancelMutation.variables === job.id) ||
                    (resumeMutation.isPending &&
                      resumeMutation.variables === job.id)
                  }
                  onCancel={() => cancelMutation.mutate(job.id)}
                  onResume={() => resumeMutation.mutate(job.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {listQuery.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={listQuery.isFetchingNextPage}
            onClick={() => void listQuery.fetchNextPage()}
          >
            {listQuery.isFetchingNextPage ? "Lade…" : "Mehr laden"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function JobRow({
  job,
  busy,
  onCancel,
  onResume,
}: {
  job: ResearchJob;
  busy: boolean;
  onCancel: () => void;
  onResume: () => void;
}) {
  return (
    <tr className="hover:bg-gray-50/80">
      <td className="px-4 py-3">
        <Link
          to={`/research-jobs/${job.id}`}
          className="font-medium text-violet-700 hover:underline"
        >
          {job.query}
        </Link>
        <div className="text-xs text-gray-500">{job.region}</div>
      </td>
      <td className="px-4 py-3">
        <JobStatusBadge status={job.status} />
      </td>
      <td className="px-4 py-3 text-gray-600">{job.current_stage}</td>
      <td className="px-4 py-3 text-gray-600">
        {job.candidates_processed}/{job.candidates_found}
        <span className="text-gray-400"> · Ziel {job.target_count}</span>
      </td>
      <td className="px-4 py-3 text-gray-600">
        {formatCostUsd(job.cost_usd_estimate)}
      </td>
      <td className="px-4 py-3 text-gray-500">{formatJobDate(job.created_at)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/research-jobs/${job.id}`}
            className="text-xs font-medium text-violet-700 hover:underline"
          >
            Öffnen
          </Link>
          {canCancelResearchJob(job.status) ? (
            <button
              type="button"
              className="text-xs font-medium text-red-800 hover:underline disabled:opacity-50"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </button>
          ) : null}
          {canResumeResearchJob(job.status) ? (
            <button
              type="button"
              className="text-xs font-medium text-sky-800 hover:underline disabled:opacity-50"
              disabled={busy}
              onClick={onResume}
            >
              Resume
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
